import Phaser from 'phaser';
import type { BackgroundMotif } from '@/types';

/** Draws a small, recognizable-at-a-glance holiday/season icon centered at (cx, cy)
 * with characteristic size `r`. Kept intentionally simple (a handful of filled
 * primitives per shape) so it reads clearly even at the small floating-icon scale
 * used in Background.ts, and cheap enough to bake into a texture once and reuse. */
function drawMotifShape(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  r: number,
  motif: BackgroundMotif,
  color: number,
  color2?: number
): void {
  switch (motif) {
    case 'heart': {
      g.fillStyle(color, 1);
      const lobeR = r * 0.55;
      g.fillCircle(cx - r * 0.5, cy - r * 0.25, lobeR);
      g.fillCircle(cx + r * 0.5, cy - r * 0.25, lobeR);
      g.fillTriangle(cx - r * 1.05, cy - r * 0.1, cx + r * 1.05, cy - r * 0.1, cx, cy + r * 1.1);
      break;
    }
    case 'snowflake': {
      g.lineStyle(Math.max(2, r * 0.12), color, 1);
      for (let i = 0; i < 3; i++) {
        const angle = (i * Math.PI) / 3;
        const dx = Math.cos(angle) * r;
        const dy = Math.sin(angle) * r;
        g.lineBetween(cx - dx, cy - dy, cx + dx, cy + dy);
        for (const t of [0.6, -0.6]) {
          const px = cx + dx * t;
          const py = cy + dy * t;
          const tickAngle = angle + Math.PI / 2;
          const tdx = Math.cos(tickAngle) * r * 0.25;
          const tdy = Math.sin(tickAngle) * r * 0.25;
          g.lineBetween(px - tdx, py - tdy, px + tdx, py + tdy);
        }
      }
      break;
    }
    case 'shamrock': {
      g.fillStyle(color, 1);
      const lobeR = r * 0.42;
      for (let i = 0; i < 3; i++) {
        const angle = -Math.PI / 2 + i * ((2 * Math.PI) / 3);
        const lx = cx + Math.cos(angle) * lobeR * 0.95;
        const ly = cy + Math.sin(angle) * lobeR * 0.95;
        g.fillCircle(lx, ly, lobeR);
      }
      g.fillRect(cx - r * 0.06, cy + lobeR * 0.3, r * 0.12, r * 0.6);
      break;
    }
    case 'egg': {
      g.fillStyle(color, 1);
      g.fillEllipse(cx, cy, r * 1.3, r * 1.7);
      if (color2 !== undefined) {
        g.fillStyle(color2, 0.9);
        g.fillCircle(cx - r * 0.25, cy - r * 0.3, r * 0.16);
        g.fillCircle(cx + r * 0.2, cy + r * 0.1, r * 0.13);
        g.fillCircle(cx - r * 0.1, cy + r * 0.45, r * 0.11);
      }
      break;
    }
    case 'star': {
      const points = 5;
      const outerR = r;
      const innerR = r * 0.42;
      const path: number[] = [];
      for (let i = 0; i < points * 2; i++) {
        const radius = i % 2 === 0 ? outerR : innerR;
        const angle = -Math.PI / 2 + (i * Math.PI) / points;
        path.push(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
      }
      g.fillStyle(color, 1);
      g.beginPath();
      g.moveTo(path[0], path[1]);
      for (let i = 2; i < path.length; i += 2) g.lineTo(path[i], path[i + 1]);
      g.closePath();
      g.fillPath();
      break;
    }
    case 'pumpkin': {
      g.fillStyle(color, 1);
      g.fillEllipse(cx, cy + r * 0.1, r * 1.5, r * 1.25);
      g.lineStyle(Math.max(1, r * 0.05), 0x000000, 0.15);
      g.lineBetween(cx - r * 0.4, cy - r * 0.55, cx - r * 0.4, cy + r * 0.75);
      g.lineBetween(cx, cy - r * 0.6, cx, cy + r * 0.8);
      g.lineBetween(cx + r * 0.4, cy - r * 0.55, cx + r * 0.4, cy + r * 0.75);
      g.fillStyle(color2 ?? 0x3a7d3a, 1);
      g.fillRect(cx - r * 0.08, cy - r * 0.75, r * 0.16, r * 0.4);
      break;
    }
    case 'leaf': {
      // Pointed-oval polygon (tip at top and bottom) reads as a leaf; a plain ellipse
      // was indistinguishable from a seed/bean at small sizes.
      const shape: [number, number][] = [
        [0, -1],
        [0.55, -0.55],
        [0.78, 0],
        [0.55, 0.55],
        [0, 1],
        [-0.55, 0.55],
        [-0.78, 0],
        [-0.55, -0.55],
      ];
      g.fillStyle(color, 1);
      g.beginPath();
      g.moveTo(cx + shape[0][0] * r, cy + shape[0][1] * r);
      for (let i = 1; i < shape.length; i++) g.lineTo(cx + shape[i][0] * r, cy + shape[i][1] * r);
      g.closePath();
      g.fillPath();

      g.lineStyle(Math.max(1, r * 0.07), color2 ?? 0x5c2e14, 0.8);
      g.lineBetween(cx, cy - r * 0.95, cx, cy + r * 0.95);
      g.lineBetween(cx, cy - r * 0.95, cx, cy - r * 1.2);
      break;
    }
    case 'gift': {
      g.fillStyle(color, 1);
      g.fillRoundedRect(cx - r * 0.7, cy - r * 0.5, r * 1.4, r * 1.1, r * 0.15);
      g.fillStyle(color2 ?? 0xffffff, 1);
      g.fillRect(cx - r * 0.12, cy - r * 0.5, r * 0.24, r * 1.1);
      g.fillRect(cx - r * 0.7, cy - r * 0.08, r * 1.4, r * 0.2);
      g.fillTriangle(cx, cy - r * 0.5, cx - r * 0.35, cy - r * 0.78, cx - r * 0.05, cy - r * 0.5);
      g.fillTriangle(cx, cy - r * 0.5, cx + r * 0.35, cy - r * 0.78, cx + r * 0.05, cy - r * 0.5);
      break;
    }
    case 'firework': {
      const n = 8;
      g.lineStyle(Math.max(2, r * 0.08), color, 1);
      for (let i = 0; i < n; i++) {
        const angle = i * ((2 * Math.PI) / n);
        const dx = Math.cos(angle) * r;
        const dy = Math.sin(angle) * r;
        g.lineBetween(cx, cy, cx + dx, cy + dy);
        g.fillStyle(i % 2 === 0 ? color : color2 ?? color, 1);
        g.fillCircle(cx + dx, cy + dy, r * 0.12);
      }
      g.fillStyle(color2 ?? color, 1);
      g.fillCircle(cx, cy, r * 0.15);
      break;
    }
    case 'sun': {
      g.fillStyle(color, 1);
      g.fillCircle(cx, cy, r * 0.55);
      g.lineStyle(Math.max(2, r * 0.1), color, 1);
      const n = 8;
      for (let i = 0; i < n; i++) {
        const angle = i * ((2 * Math.PI) / n);
        const x1 = cx + Math.cos(angle) * r * 0.72;
        const y1 = cy + Math.sin(angle) * r * 0.72;
        const x2 = cx + Math.cos(angle) * r * 1.05;
        const y2 = cy + Math.sin(angle) * r * 1.05;
        g.lineBetween(x1, y1, x2, y2);
      }
      break;
    }
    case 'flower': {
      g.fillStyle(color, 1);
      const petalR = r * 0.35;
      for (let i = 0; i < 5; i++) {
        const angle = i * ((2 * Math.PI) / 5) - Math.PI / 2;
        const px = cx + Math.cos(angle) * r * 0.5;
        const py = cy + Math.sin(angle) * r * 0.5;
        g.fillCircle(px, py, petalR);
      }
      g.fillStyle(color2 ?? 0xffe066, 1);
      g.fillCircle(cx, cy, r * 0.32);
      break;
    }
    case 'none':
    default:
      break;
  }
}

/** Bakes a motif icon into a small square texture (cached by motif+colors) and
 * returns its texture key, so many floating instances can share one draw call's
 * worth of geometry instead of re-drawing Graphics every frame. */
export function ensureMotifTexture(
  scene: Phaser.Scene,
  motif: BackgroundMotif,
  color: number,
  color2?: number,
  size = 72
): string {
  const key = `motif-${motif}-${color}-${color2 ?? 'x'}`;
  if (scene.textures.exists(key)) return key;

  const g = scene.add.graphics();
  drawMotifShape(g, size / 2, size / 2, size * 0.42, motif, color, color2);
  g.generateTexture(key, size, size);
  g.destroy();
  return key;
}
